import React, { forwardRef } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import ICON_REGISTRY from '../../utils/iconRegistry';
import { formatTabLabel } from './tabLabels';

/**
 * Individual Tab Component
 * 
 * Displays a single tab with title, icon, and close button
 * Supports keyboard navigation via roving tabindex pattern
 */
interface TabData {
  icon?: string;
  title: string;
  pinned?: boolean;
}

interface TabProps {
  tab: TabData;
  /** Identity cue rendered only when another open tab has the same title. */
  disambiguator?: string | null;
  active?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onClose?: () => void;
  onPin?: () => void;
  tabIndex?: number;
}

const Tab = forwardRef(function Tab({ tab, disambiguator, active, onClick, onClose, onPin, tabIndex }: TabProps, ref) {
  const fullLabel = formatTabLabel(tab.title, disambiguator);
  const Icon = tab.icon ? ICON_REGISTRY[tab.icon] || null : null;
  
  const handleClose = (e) => {
    e.stopPropagation();
    if (!tab.pinned) {
      onClose();
    }
  };

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      onClick={(e) => {
        onClick(e);
      }}
      className={`
        group flex items-center gap-2 px-3.5 py-2 min-w-[120px] ${disambiguator ? 'max-w-[280px]' : 'max-w-[200px]'} 
        cursor-pointer select-none transition-all rounded-lg
        focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500
        ${active 
          ? 'bg-kindredly-blue dark:bg-kindredly-blue-dark text-white shadow-sm' 
          : 'text-gray-500 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-gray-700/50 hover:text-gray-800 dark:hover:text-gray-200'
        }
      `}
      role="tab"
      aria-selected={active}
      tabIndex={tabIndex !== undefined ? tabIndex : (active ? 0 : -1)}
    >
      {Icon && (
        <Icon 
          className={`flex-shrink-0 ${active ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`}
          style={{ fontSize: '18px' }} 
        />
      )}
      
      <Tooltip title={fullLabel} enterDelay={600} disableInteractive>
        <span className="flex flex-1 min-w-0 items-baseline gap-1 text-sm font-medium">
          <span className="truncate">{tab.title}</span>
          {disambiguator && (
            <span
              className={`flex-shrink-0 text-xs font-normal ${active ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'}`}
              data-testid="tab-disambiguator"
            >
              · {disambiguator}
            </span>
          )}
        </span>
      </Tooltip>
      
      {tab.pinned && (
        <PushPinIcon 
          className={`flex-shrink-0 ${active ? 'text-white' : 'text-blue-500'}`}
          style={{ fontSize: '14px' }} 
        />
      )}
      
            <Tooltip title={tab.pinned ? "Cannot close pinned tab" : "Close tab"}>
              <IconButton
                size="small"
                onClick={handleClose}
                disabled={tab.pinned}
                aria-label={`Close ${fullLabel} tab`}
                tabIndex={-1}
                className={`
                  flex-shrink-0 p-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity
                  ${tab.pinned ? 'cursor-not-allowed' : ''}
                `}
                sx={{ 
                  minWidth: '20px', 
                  minHeight: '20px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CloseIcon style={{ fontSize: '16px', color: active ? '#fff' : undefined }} />
              </IconButton>
            </Tooltip>
    </div>
  );
});

export default Tab;
